import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { dateTime, humanise, inr } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Button, Field, Modal, Notice, SearchInput, Select, StatusBadge, Textarea, useToast } from '../components/ui';

interface PaymentRow {
  id: string;
  purpose: string;
  baseAmountPaise: number;
  gstPaise: number;
  amountPaise: number;
  gstPercent: string;
  status: string;
  gateway: string;
  gatewayOrderId: string;
  gatewayPaymentId: string | null;
  failureReason: string | null;
  method: string | null;
  methodDetail: string | null;
  failureCode: string | null;
  attemptCount: number;
  lastAttemptAt: string | null;
  activated: boolean;
  activatedAs: string | null;
  capturedAt: string | null;
  refundedAt: string | null;
  createdAt: string;
  user: { id: string; mobile: string; displayName: string | null };
}

export function PaymentsPage() {
  const { get, set } = useListParams();
  const [refunding, setRefunding] = useState<PaymentRow | null>(null);
  const [reason, setReason] = useState('');
  const toast = useToast();
  const qc = useQueryClient();

  const status = get('status');
  const purpose = get('purpose');
  const search = get('search');

  /** Asks Razorpay how the order stands, and fulfils it if it was paid. */
  const check = useMutation({
    mutationFn: (id: string) => api.post<{ outcome: string; failureReason: string | null }>(`/admin/payments/${id}/reconcile`),
    onSuccess: (r) => {
      toast(
        r.outcome === 'SUCCESS'
          ? 'Paid — fulfilled now'
          : r.outcome === 'FAILED'
            ? `Failed: ${r.failureReason ?? 'no reason given'}`
            : r.outcome === 'PENDING'
              ? 'Still pending at Razorpay'
              : 'Not attempted yet',
        r.outcome === 'SUCCESS' ? 'success' : 'info',
      );
      qc.invalidateQueries({ queryKey: ['payments'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const refund = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.post(`/admin/payments/${id}/refund`, { reason }),
    onSuccess: () => {
      toast('Recorded as refunded — raise the refund in the gateway', 'success');
      setRefunding(null);
      setReason('');
      qc.invalidateQueries({ queryKey: ['payments'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const columns: Array<Column<PaymentRow>> = [
    {
      key: 'user',
      header: 'Paid by',
      render: (r) => (
        <div>
          <Link to={`/users/${r.user.id}`}>{r.user.displayName ?? 'Unnamed'}</Link>
          <div className="cell-muted">{r.user.mobile}</div>
        </div>
      ),
    },
    { key: 'purpose', header: 'For', render: (r) => humanise(r.purpose) },
    { key: 'base', header: 'Base', numeric: true, render: (r) => inr(r.baseAmountPaise, { paise: true }) },
    {
      key: 'gst',
      header: 'GST',
      numeric: true,
      render: (r) => (
        <span title={`${r.gstPercent}%`}>{inr(r.gstPaise, { paise: true })}</span>
      ),
    },
    {
      key: 'total',
      header: 'Total',
      numeric: true,
      render: (r) => <span className="cell-strong">{inr(r.amountPaise, { paise: true })}</span>,
    },
    {
      key: 'method',
      header: 'Method',
      render: (r) =>
        r.method ? (
          <div>
            <div>{humanise(r.method)}</div>
            {r.methodDetail && <div className="cell-muted">{r.methodDetail}</div>}
          </div>
        ) : (
          <span className="cell-muted">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <div>
          {/* An open order nobody has finished paying is pending, not failed. */}
          <StatusBadge value={r.status === 'CREATED' ? (r.attemptCount > 0 ? 'PENDING' : 'CREATED') : r.status} />
          {r.status === 'FAILED' && r.failureReason && (
            <div className="cell-muted cell-clip" title={r.failureReason} style={{ maxWidth: 260 }}>
              {r.failureReason}
              {r.failureCode ? ` (${r.failureCode})` : ''}
            </div>
          )}
          {r.attemptCount > 1 && <div className="cell-muted">{r.attemptCount} attempts</div>}
        </div>
      ),
    },
    {
      key: 'activated',
      header: 'Activated',
      render: (r) =>
        r.activated ? (
          <div>
            <StatusBadge value="ACTIVE" />
            {r.activatedAs && <div className="cell-mono">{r.activatedAs}</div>}
          </div>
        ) : (
          <span className="cell-muted">No</span>
        ),
    },
    {
      key: 'ref',
      header: 'Gateway',
      render: (r) => (
        <div>
          <div className="cell-mono cell-clip" title={r.gatewayOrderId}>
            {r.gatewayOrderId}
          </div>
          {r.gatewayPaymentId && (
            <div className="cell-mono cell-clip cell-muted" title={r.gatewayPaymentId}>
              {r.gatewayPaymentId}
            </div>
          )}
          <div className="cell-muted">{r.gateway}</div>
        </div>
      ),
    },
    {
      key: 'when',
      header: 'When',
      render: (r) => <span className="cell-muted">{dateTime(r.capturedAt ?? r.lastAttemptAt ?? r.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (r) =>
        r.status === 'CAPTURED' ? (
          <div className="cell-actions">
            <Button size="sm" onClick={() => setRefunding(r)}>
              Refund…
            </Button>
          </div>
        ) : r.gateway === 'razorpay' && r.status !== 'REFUNDED' ? (
          <div className="cell-actions">
            <Button size="sm" loading={check.isPending && check.variables === r.id} onClick={() => check.mutate(r.id)}>
              Check with Razorpay
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <DataTable<PaymentRow>
        queryKey={['payments', status, purpose, search]}
        path="/admin/payments"
        params={{ status, purpose, search }}
        columns={columns}
        emptyIcon="₹"
        emptyTitle="No payments match"
        footer={(page) => {
          const sum = page.meta.sumAmountPaise as number | undefined;
          const gst = page.meta.sumGstPaise as number | undefined;
          if (sum === undefined) return null;
          return (
            <div className="row" style={{ padding: '12px var(--s5)', borderTop: '1px solid var(--border)', gap: 'var(--s6)' }}>
              <span className="cell-muted">Totals for this filter</span>
              <div className="spacer" />
              <span>
                GST <strong>{inr(gst ?? 0)}</strong>
              </span>
              <span>
                Gross <strong>{inr(sum)}</strong>
              </span>
            </div>
          );
        }}
        toolbar={
          <>
            <SearchInput value={search} onChange={(v) => set('search', v)} placeholder="Order or payment id, name or mobile…" />
            <Select
              value={status}
              placeholder="Any status"
              onChange={(e) => set('status', e.target.value)}
              options={['CREATED', 'AUTHORIZED', 'CAPTURED', 'FAILED', 'REFUNDED'].map((v) => ({
                value: v,
                label: humanise(v),
              }))}
            />
            <Select
              value={purpose}
              placeholder="Any purpose"
              onChange={(e) => set('purpose', e.target.value)}
              options={['LISTING_FEE', 'SLOT_PURCHASE', 'BOOST', 'PROVIDER_SUB', 'AGENT_SUB'].map((v) => ({
                value: v,
                label: humanise(v),
              }))}
            />
          </>
        }
      />

      {refunding && (
        <Modal
          title="Record a refund"
          sub={`${inr(refunding.amountPaise, { paise: true })} paid by ${refunding.user.displayName ?? refunding.user.mobile}`}
          onClose={() => setRefunding(null)}
          footer={
            <>
              <Button onClick={() => setRefunding(null)}>Cancel</Button>
              <Button
                variant="danger"
                loading={refund.isPending}
                disabled={!reason.trim()}
                onClick={() => refund.mutate({ id: refunding.id, reason: reason.trim() })}
              >
                Mark refunded
              </Button>
            </>
          }
        >
          <div className="stack">
            <Notice tone="warn" title="This does not move money">
              It marks our record refunded so the ledger and GST report agree. Raise the actual refund in the{' '}
              {refunding.gateway} dashboard against order <span className="cell-mono">{refunding.gatewayOrderId}</span>.
              Doing it this way round means our records can never claim a refund the bank did not make.
            </Notice>
            <Field label="Reason" hint="Recorded in the audit trail.">
              <Textarea value={reason} autoFocus onChange={(e) => setReason(e.target.value)} />
            </Field>
          </div>
        </Modal>
      )}
    </>
  );
}
